import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import type { IncidentStatus, IncidentSummary, IncidentUpdate, ServiceStatus } from './types.js';
import { formatTimestamp, relativeTime } from './format.js';

const STATUS_LABEL: Record<IncidentStatus, string> = {
  investigating: 'Investigating',
  identified: 'Cause identified',
  monitoring: 'Fix deployed — monitoring',
  resolved: 'Resolved',
};

const SEVERITY_COLOR: Record<IncidentSummary['severity'], 'error' | 'warning' | 'default'> = {
  critical: 'error',
  major: 'warning',
  minor: 'warning',
  maintenance: 'default',
};

interface Props {
  incidents: IncidentSummary[];
  services: ServiceStatus[];
}

function durationMinutes(started: string, ended: string): number {
  return Math.max(1, Math.round((new Date(ended).getTime() - new Date(started).getTime()) / 60_000));
}

function tail(inc: IncidentSummary): string {
  if (inc.resolved_at) {
    const min = durationMinutes(inc.started_at, inc.resolved_at);
    return `Resolved ${formatTimestamp(inc.resolved_at)} · ${min} min total`;
  }
  return `Started ${formatTimestamp(inc.started_at)} · ${relativeTime(inc.started_at)}`;
}

function affectedDisplay(serviceIds: string[], services: ServiceStatus[]): string {
  return serviceIds
    .map((id) => services.find((s) => s.service === id)?.display ?? id)
    .join(', ');
}

function Timeline({ updates }: { updates: IncidentUpdate[] }) {
  if (updates.length === 0) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Stepper orientation="vertical" activeStep={updates.length} nonLinear>
        {updates.map((u) => (
          <Step key={u.created_at} completed>
            <StepLabel>
              <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                {STATUS_LABEL[u.status]}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
                {formatTimestamp(u.created_at)} · {relativeTime(u.created_at)}
              </Typography>
            </StepLabel>
            {u.note && (
              <StepContent>
                <Typography variant="body2" color="text.secondary">{u.note}</Typography>
              </StepContent>
            )}
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}

/**
 * Renders both active and resolved incidents in the "Incidents" tab.
 * Newest-first. Resolved incidents show duration; active ones show
 * "started X min ago". When the row has a body, it renders below the
 * timestamps as italic body text — used for the post-mortem summary
 * after an incident resolves.
 */
export function IncidentsList({ incidents, services }: Props) {
  if (incidents.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No incidents recorded yet.
      </Typography>
    );
  }

  return (
    <List disablePadding>
      {incidents.map((inc, idx) => (
        <ListItem
          key={inc.id}
          disableGutters
          divider={idx < incidents.length - 1}
          alignItems="flex-start"
          sx={{ py: 2 }}
        >
          <ListItemText
            disableTypography
            primary={
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  color={SEVERITY_COLOR[inc.severity]}
                  label={inc.severity}
                  sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{inc.title}</Typography>
                <Typography variant="body2" color="text.secondary">— {inc.current_status}</Typography>
              </Box>
            }
            secondary={
              <Box sx={{ mt: 0.5 }}>
                {inc.affected_services.length > 0 && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Affecting {affectedDisplay(inc.affected_services, services)}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" component="div">
                  {tail(inc)}
                </Typography>
                {inc.body && (
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{ mt: 1, fontStyle: 'italic', color: 'text.primary' }}
                  >
                    {inc.body}
                  </Typography>
                )}
                <Timeline updates={inc.updates} />
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  );
}
